import { ActiveRecord, sources } from '@mysidewalk/active-record';
import { DataReference } from '../data/index.js';
import Attribution from './Attribution.js';
import CharacteristicType from './CharacteristicType.js';
import TimeElement from './TimeElement.js';
/**
 * @classdesc
 * Columns connect a CharacteristicType to a single TimeElement, helping create
 * a specific observation. e.g. CharacteristicType == "Total Population",
 * TimeElement == "2010", etc.
 *
 * @memberof system-metadata
 * @property {number} id
 * @property {system-metadata.Attribution} attribution
 * @property {system-metadata.CharacteristicType} characteristicType
 * @property {system-metadata.Citation} citation
 * @property {system-metadata.TimeElement} timeElement
 */
class Column extends ActiveRecord {
    initialize(props) {
        super.initialize(props);
        this.attribution = new Attribution(this.attribution);
        this.characteristicType = new CharacteristicType(this.characteristicType);
        this.timeElement = new TimeElement(this.timeElement);
    }
    /**
     * @type {boolean}
     */
    get isProjection() {
        return this.timeElement.isProjection;
    }
    getDefaultNormalizers() {
        return Column.getDefaultNormalizers([this]);
    }
    /**
     * @return {data-references.DataReference}
     */
    toDataReference() {
        return new DataReference({
            type: DataReference.SourceType.SYSTEM,
            column: this.id,
        });
    }
    /**
     * @param {system-metadata.Column[]} columns
     * @return {system-metadata.Column[]}
     */
    static getDefaultNormalizers(columns) {
        const columnIDs = columns.map(column => column.id);
        return Column.where({ normalizationFor: columnIDs });
    }
    /**
     * @param {number} timeElementID
     * @return {system-metadata.Column[]}
     */
    static getDefaultNormalizersForTimeElement(timeElementID) {
        return Column.where({ defaultNormalizersForTimeElement: timeElementID });
    }
    /**
     * @param {data-references.DataReference} dataReference
     * @return {Promise<system-metadata.Column>}
     */
    static forReference(dataReference) {
        return new Promise((resolve, reject) => {
            const defer = { resolve, reject };
            consolidate(dataReference.column, null, defer);
        });
    }
}
Column.$AR = {
    url: '',
    SourceClass: sources.ActiveRecordJSONAPISource,
};
let next = [];
let timerID = null;
function makeConsolidatedRequests() {
    const requests = [...next];
    next = [];
    // Build out the parameters
    const ids = new Set();
    const included = new Set();
    const addToInclude = included.add.bind(included);
    requests.forEach(({ id, include }) => {
        ids.add(id);
        if (include) {
            include.split(',').forEach(addToInclude);
        }
    });
    const params = {
        id__in: [...ids.values()].sort().join(','),
        include: [...included.values()].sort().join(','),
        itemsPerPage: MAX_REQUEST_BUNDLE,
    };
    if (params.include === '') {
        delete params.include;
    }
    // Resolve the promise for each "request" after finding the matching result
    return Column.all(params)
        .then((columns) => {
        const byID = columns.reduce((memo, column) => {
            memo[column.id] = column;
            return memo;
        }, {});
        requests.forEach(({ id, deferral }) => {
            const match = byID[id];
            const hasMatch = !!match;
            const method = hasMatch ? deferral.resolve : deferral.reject;
            const result = hasMatch ? match : new Error(`[Not Found] Column ${id}`);
            method.call(deferral, result);
        });
    });
}
const MAX_REQUEST_BUNDLE = 20; // IDs & "include" parameter
const MAX_DELAY = 50; // milliseconds
function consolidate(id, include, deferral) {
    // Add request to list to be processed
    next.push({ id, include, deferral });
    timerID && clearTimeout(timerID);
    timerID = null;
    // Beacuse of limitations of URI length (HTTP Error 414), set max count
    if (next.length >= MAX_REQUEST_BUNDLE) {
        makeConsolidatedRequests();
    }
    else {
        // Wait MAX_DELAY for any additional "requests", then make API call
        timerID = setTimeout(makeConsolidatedRequests, MAX_DELAY);
    }
}
export default Column;
